/**
 * BrandSmartLocatePage.tsx
 *
 * Full-page stepper for the "Locate My Assets" flow.
 * Steps:
 *   1 → Enter contact (email or phone)
 *   2 → Verify OTP
 *   3 → Select assets
 *   4 → Resolve address conflicts
 *   5 → Done
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';

import {
  BrandSmartLookupPurchase,
  BrandSmartImportResponseData,
} from '../../services/brandsmart.api';

import LocateStep1Contact from './LocateStep1Contact';
import LocateStep2Verify from './LocateStep2Verify';
import LocateStep3Assets from './LocateStep3Assets';
import LocateStep4Address from './LocateStep4Address';
import LocateStep5Done from './LocateStep5Done';

// ─── State shape ──────────────────────────────────────────────────────────────
export interface LocateState {
  step: 1 | 2 | 3 | 4 | 5;
  contact: string;
  contactType: 'email' | 'phone';
  foundPurchases: BrandSmartLookupPurchase[];
  selectedIds: string[];
  result: BrandSmartImportResponseData | null;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function BrandSmartLocatePage() {
  const navigate = useNavigate();
  const { t } = useTranslation('brandsmart');

  const STEPS = useMemo(() => [
    { n: 1, label: t('locatePage.steps.contact') },
    { n: 2, label: t('locatePage.steps.verify') },
    { n: 3, label: t('locatePage.steps.select') },
    { n: 4, label: t('locatePage.steps.address') },
    { n: 5, label: t('locatePage.steps.done') },
  ], [t]);

  const [state, setState] = useState<LocateState>({
    step: 1,
    contact: '',
    contactType: 'email',
    foundPurchases: [],
    selectedIds: [],
    result: null,
  });

  function advance(patch: Partial<LocateState>) {
    setState((prev) => ({ ...prev, ...patch }));
  }

  // Determine if step 4 (address) is needed at all
  function needsAddressStep(purchases: BrandSmartLookupPurchase[], selectedIds: string[]) {
    const selected = purchases.filter((p) => selectedIds.includes(p.id));
    const uniqueAddresses = new Set(
      selected.map((p) => `${p.deliveryCity.toLowerCase()}_${p.deliveryZip}`),
    );
    // If all selected purchases share the same address key or only one address — skip
    return uniqueAddresses.size > 0; // always show so user can choose which home
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-muted/40 border-b">
        <div className="max-w-2xl mx-auto px-6 lg:px-8 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-background transition-colors flex-shrink-0"
          >
            <ArrowLeft className="h-5 w-5 text-foreground" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-foreground">{t('locatePage.title')}</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('locatePage.subtitle')}
            </p>
          </div>
        </div>
      </header>

      {/* Stepper indicator */}
      {state.step < 5 && (
        <div className="border-b bg-background px-6 lg:px-8 py-3">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            {STEPS.filter((s) => s.n < 5).map((s, i, arr) => (
              <React.Fragment key={s.n}>
                <div className="flex flex-col items-center gap-1 min-w-0">
                  <div
                    className={[
                      'h-7 w-7 rounded-full flex items-center justify-center text-xs font-medium transition-colors',
                      state.step > s.n
                        ? 'bg-primary text-primary-foreground'
                        : state.step === s.n
                        ? 'bg-primary text-primary-foreground ring-2 ring-primary/30'
                        : 'bg-muted text-muted-foreground',
                    ].join(' ')}
                  >
                    {state.step > s.n ? '✓' : s.n}
                  </div>
                  <span
                    className={`text-[10px] font-medium ${
                      state.step === s.n ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {i < arr.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 transition-colors ${
                      state.step > s.n ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Step content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        {state.step === 1 && (
          <LocateStep1Contact
            onNext={(contact, contactType) =>
              advance({ contact, contactType, step: 2 })
            }
          />
        )}
        {state.step === 2 && (
          <LocateStep2Verify
            contact={state.contact}
            contactType={state.contactType}
            onBack={() => advance({ step: 1 })}
            onVerified={(purchases) =>
              advance({ foundPurchases: purchases, step: 3 })
            }
          />
        )}
        {state.step === 3 && (
          <LocateStep3Assets
            purchases={state.foundPurchases}
            onBack={() => advance({ step: 2 })}
            onNext={(selectedIds) => {
              const nextStep = needsAddressStep(state.foundPurchases, selectedIds) ? 4 : 5;
              advance({ selectedIds, step: nextStep as 4 | 5 });
            }}
          />
        )}
        {state.step === 4 && (
          <LocateStep4Address
            purchases={state.foundPurchases.filter((p) =>
              state.selectedIds.includes(p.id),
            )}
            selectedIds={state.selectedIds}
            onBack={() => advance({ step: 3 })}
            onDone={(result) => advance({ result, step: 5 })}
          />
        )}
        {state.step === 5 && (
          <LocateStep5Done
            result={state.result}
            onViewAssets={() => navigate('/assets')}
          />
        )}
      </div>
    </div>
  );
}
